// shell/components/renderer-utils.ts

import { ColumnDef } from './table';
import { HeatMapBounds, HeatMapShader } from './heat-map';

/**
 * Compute shade rank for heat map coloring.
 *
 * When a column has a custom `heatMap.valueFn`, that function is used to
 * extract the numeric rank from the item directly — matching how bounds
 * are computed in ColumnManager.  Without `valueFn`, the raw display
 * value is ranked via HeatMapShader's static type-based methods.
 */
export function computeShadeRank<T>(
    value: unknown,
    bounds: HeatMapBounds | undefined,
    column?: ColumnDef<T>,
    item?: T,
): number | undefined {
    if (!bounds) return undefined;

    // When a custom valueFn is provided, use it to rank the item directly.
    // This keeps the shade rank on the same numeric scale as the bounds
    // (which are also computed via valueFn in ColumnManager).
    if (column?.heatMap?.valueFn && item != null) {
        const raw = column.heatMap.valueFn(item);
        if (raw == null) return undefined;
        return rankRawValue(raw);
    }

    if (value == null) return undefined;
    return rankRawValue(value);
}

/**
 * Rank a raw value using HeatMapShader's type-based ranking.
 */
function rankRawValue(value: unknown): number | undefined {
    if (typeof value === 'number') {
        return HeatMapShader.rankNumber(value);
    }

    if (value instanceof Date) {
        return HeatMapShader.rankDate(value);
    }

    if (typeof value === 'string') {
        // Check if it looks like an IP address
        if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(value)) {
            return HeatMapShader.rankIpAddress(value);
        }
        return HeatMapShader.rankString(value);
    }

    return undefined;
}

/**
 * Safely extract value from item using column's getValue.
 * Returns null on error and logs the failure.
 */
export function safeGetValue<T>(column: ColumnDef<T>, item: T): unknown {
    try {
        return column.getValue(item);
    } catch (e) {
        console.error(`getValue failed for column ${column.key}:`, e);
        return null;
    }
}

/**
 * Find insertion position in a sorted array of indices.
 * Uses binary search for O(log n) performance.
 */
export function findInsertionIndex(sortedIndices: number[], targetIndex: number): number {
    let low = 0;
    let high = sortedIndices.length;

    while (low < high) {
        const mid = (low + high) >>> 1;
        if (sortedIndices[mid] < targetIndex) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }

    return low;
}

/**
 * Manages timeouts for row/card state animations.
 * Ensures proper cleanup to prevent memory leaks.
 */
export class AnimationTimeoutManager {
    private timeouts = new Map<string, number>();
    private disposed = false;

    /**
     * Schedule a callback after delay, cancelling any existing timeout for the key.
     */
    schedule(key: string, callback: () => void, delayMs: number): void {
        if (this.disposed) return;

        this.cancel(key);
        const id = window.setTimeout(() => {
            this.timeouts.delete(key);
            callback();
        }, delayMs);
        this.timeouts.set(key, id);
    }

    /**
     * Cancel a scheduled timeout.
     */
    cancel(key: string): void {
        const id = this.timeouts.get(key);
        if (id !== undefined) {
            clearTimeout(id);
            this.timeouts.delete(key);
        }
    }

    /**
     * Cancel all scheduled timeouts without disposing.
     * Use this when clearing state but keeping the manager alive.
     */
    cancelAll(): void {
        for (const id of this.timeouts.values()) {
            clearTimeout(id);
        }
        this.timeouts.clear();
    }

    /**
     * Cancel all scheduled timeouts and mark as disposed.
     * No new timeouts can be scheduled after this.
     */
    dispose(): void {
        this.cancelAll();
        this.disposed = true;
    }
}

/**
 * Tracks event listeners for proper cleanup.
 */
export class EventListenerTracker {
    private listeners: Array<{
        element: EventTarget;
        type: string;
        listener: EventListener;
        options?: boolean | AddEventListenerOptions;
    }> = [];

    add(
        element: EventTarget,
        type: string,
        listener: EventListener,
        options?: boolean | AddEventListenerOptions
    ): void {
        element.addEventListener(type, listener, options);
        this.listeners.push({ element, type, listener, options });
    }

    dispose(): void {
        for (const { element, type, listener, options } of this.listeners) {
            element.removeEventListener(type, listener, options);
        }
        this.listeners = [];
    }
}

/**
 * Default duration for fresh highlight animation in ms.
 */
export const FRESH_HIGHLIGHT_DURATION_MS = 2000;
